import OncolensToast from '@/components/OncolensToast';
import { useConferenceOrderNotifications } from '@/hooks/featureFlags/active-flags/useConferenceOrderNotifications';
import { useV2ConferenceEmails } from '@/hooks/featureFlags/active-flags/useV2ConferenceEmails';
import { useV2JoinMeeting } from '@/hooks/featureFlags/active-flags/useV2JoinMeeting';
import { yupResolver } from '@hookform/resolvers/yup';
import Button from '@oncolens/oncolens-ui/lib/components/FormControls/Button';
import ButtonWithLoading from '@oncolens/oncolens-ui/lib/components/FormControls/ButtonWithLoading';
import Checkbox from '@oncolens/oncolens-ui/lib/components/FormControls/Checkbox';
import Listbox from '@oncolens/oncolens-ui/lib/components/FormControls/Listbox';
import TextInput from '@oncolens/oncolens-ui/lib/components/FormControls/TextInput';
import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { isNil } from 'lodash';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import {
  useGetAccreditationSpecialtiesList,
  useGetSpecialtiesList,
} from '../api/getSpecialtiesList';
import { useGetUser } from '../api/getUser';
import { useSyncUser } from '../api/syncUser';
import { useUpdateUserProfile } from '../api/updateUserProfile';
import { UserProfileForUpdateDto } from '../types';

interface ProfileFormProps {
  userId: string | undefined;
}

const profileSchema = yup.object<Record<keyof UserProfileForUpdateDto, yup.AnySchema>>({
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
  phoneNumber: yup.string().nullable(),
  npi: yup
    .string()
    .test(
      'npi',
      'Npi must be valid',
      (npi) => npi?.length === 0 || (/^\d{10}$/.test(String(npi)) && npi?.length === 10),
    )
    .nullable(),
  specialty: yup.string().required('Specialty is required'),
  accreditationSpecialty: yup.string().required('Accreditation Specialty is required'),
  employer: yup.string().nullable(),
  userId: yup.string().nullable(),
  shouldReceiveConferenceOrderNotifications: yup.boolean(),
  shouldReceiveAllOtherNotifications: yup.boolean(),
  adminZoomJoinMethod: yup.string().nullable().oneOf(['Host', 'Participant', null]),
});

function UserProfileForm({ userId }: ProfileFormProps) {
  const { data: user } = useGetUser(userId);
  const { data: specialtiesData } = useGetSpecialtiesList();
  const { data: accreditationSpecialtiesData } = useGetAccreditationSpecialtiesList();
  const specialties = (specialtiesData || []).map((specialty) => ({
    value: specialty,
    label: specialty,
  }));
  const accreditationSpecialties = (accreditationSpecialtiesData || []).map(
    (accreditationSpecialty) => ({
      value: accreditationSpecialty,
      label: accreditationSpecialty,
    }),
  );

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
  } = useForm<UserProfileForUpdateDto>({
    resolver: yupResolver(profileSchema),
  });

  const updateUserProfileMutation = useUpdateUserProfile(user?.data.userProfile?.id);
  const syncUserMutation = useSyncUser(user?.data.legacyId);
  const onSubmit = (data: UserProfileForUpdateDto) => {
    const payload = isOncolensAdmin ? data : { ...data, adminZoomJoinMethod: undefined };
    updateUserProfileMutation.mutate(payload, {
      onSuccess: () => OncolensToast.success('User saved'),
      onError: () => OncolensToast.error('Error occurred'),
    });
  };
  const navigate = useNavigate();

  const handleSync = () => {
    syncUserMutation.mutate(undefined as void, {
      onSuccess: () => OncolensToast.success('User synced'),
      onError: (e) => {
        OncolensToast.error(
          isNil(e?.message) ? 'An unknown error ocurred' : `An error occurred: ${e?.message}`,
        );
      },
    });
  };

  useEffect(() => {
    setValue('firstName', user?.data?.userProfile?.firstName ?? '');
    setValue('lastName', user?.data?.userProfile?.lastName ?? '');
    setValue('phoneNumber', user?.data?.userProfile?.phoneNumber ?? '');
    setValue('npi', user?.data?.userProfile?.npi ?? '');
    setValue('specialty', user?.data?.userProfile?.specialty ?? '');
    setValue('accreditationSpecialty', user?.data?.userProfile?.accreditationSpecialty ?? '');
    setValue('employer', user?.data?.userProfile?.employer ?? '');
    setValue('userId', userId ?? '');
    setValue(
      'shouldReceiveConferenceOrderNotifications',
      user?.data?.userProfile?.shouldReceiveConferenceOrderNotifications ?? false,
    );
    setValue(
      'shouldReceiveAllOtherNotifications',
      user?.data?.userProfile?.shouldReceiveAllOtherNotifications ?? false,
    );
    setValue('adminZoomJoinMethod', user?.data?.userProfile?.adminZoomJoinMethod ?? undefined);
  }, [setValue, user, userId]);

  const confOrderEmailManagementEnabled = useConferenceOrderNotifications();
  const v2ConferenceEmailsEnabled = useV2ConferenceEmails();
  const v2JoinMeetingEnabled = useV2JoinMeeting();
  const isOncolensAdmin = user?.data?.orgRoles?.some((r) => r.role === 'Oncolens Admin') ?? false;

  return (
    <div className="">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 gap-y-6 sm:grid-cols-6 sm:gap-x-6">
          <div className="sm:col-span-3">
            <Controller
              name="firstName"
              control={control}
              render={({ field }) => (
                <TextInput
                  id="userProfile.firstName"
                  type="text"
                  htmlName="firstName"
                  label="First Name"
                  required={true}
                  error={errors?.firstName?.message}
                  value={field.value}
                  onChange={field.onChange}
                  wrapperProps={{ className: 'col-span-7 md:col-span-3' }}
                />
              )}
            />
          </div>

          <div className="sm:col-span-3">
            <Controller
              name="lastName"
              control={control}
              render={({ field }) => (
                <TextInput
                  id="userProfile.lastName"
                  type="text"
                  htmlName="lastName"
                  label="Last Name"
                  required={true}
                  error={errors?.lastName?.message}
                  value={field.value}
                  onChange={field.onChange}
                  wrapperProps={{ className: 'col-span-7 md:col-span-3' }}
                />
              )}
            />
          </div>

          <div className="sm:col-span-3">
            <Controller
              name="phoneNumber"
              control={control}
              render={({ field }) => (
                <TextInput
                  id="userProfile.phoneNumber"
                  type="text"
                  htmlName="phoneNumber"
                  label="Phone Number"
                  required={false}
                  error={errors?.phoneNumber?.message}
                  value={field.value}
                  onChange={field.onChange}
                  wrapperProps={{ className: 'col-span-7 md:col-span-3' }}
                />
              )}
            />
          </div>

          <div className="sm:col-span-3">
            <Controller
              name="employer"
              control={control}
              render={({ field }) => (
                <TextInput
                  id="userProfile.employer"
                  type="text"
                  htmlName="employer"
                  label="Employer"
                  required={false}
                  error={errors?.employer?.message}
                  value={field.value}
                  onChange={field.onChange}
                  wrapperProps={{ className: 'col-span-7 md:col-span-3' }}
                  disabled={true}
                />
              )}
            />
          </div>

          <div className="sm:col-span-3">
            <Controller
              name="specialty"
              control={control}
              render={({ field }) => (
                <Listbox
                  label="Specialty"
                  data={specialties}
                  selected={specialties.find((item) => item.value === field.value)?.value || ''}
                  setSelected={(item) => {
                    field.onChange(item);
                  }}
                  required={true}
                  error={errors?.specialty?.message}
                />
              )}
            />
          </div>

          <div className="sm:col-span-3">
            <Controller
              name="npi"
              control={control}
              render={({ field }) => (
                <TextInput
                  id="userProfile.npi"
                  type="text"
                  htmlName="npi"
                  label="Npi"
                  required={false}
                  error={errors?.npi?.message}
                  value={field.value}
                  onChange={field.onChange}
                  wrapperProps={{ className: 'col-span-7 md:col-span-3' }}
                />
              )}
            />
          </div>

          <div className="sm:col-span-3">
            <Controller
              name="accreditationSpecialty"
              control={control}
              render={({ field }) => (
                <Listbox
                  label="Accreditation Specialty"
                  data={accreditationSpecialties}
                  selected={
                    accreditationSpecialties.find((item) => item.value === field.value)?.value || ''
                  }
                  setSelected={(item) => {
                    field.onChange(item);
                  }}
                  required={true}
                  error={errors?.accreditationSpecialty?.message}
                />
              )}
            />
          </div>

          {v2JoinMeetingEnabled && isOncolensAdmin && (
            <div className="col-span-full" data-cy="virtual-conference-settings-section">
              <span
                className="text-sm font-medium text-gray-700"
                data-cy="virtual-conference-settings-label">
                Virtual Conference Settings
              </span>
              <Controller
                name="adminZoomJoinMethod"
                control={control}
                render={({ field }) => (
                  <Checkbox
                    data-cy="disableHostJoinAccess"
                    id="adminZoomJoinMethod"
                    htmlName="adminZoomJoinMethod"
                    label="Disable Host Join Access"
                    onClick={(evt) => field.onChange(evt.target.checked ? 'Participant' : 'Host')}
                    value={field.value === 'Participant'}
                  />
                )}
              />
            </div>
          )}

          {(confOrderEmailManagementEnabled || v2ConferenceEmailsEnabled) && (
            <div className="col-span-full" data-cy="notification-settings-section">
              <span
                className="text-sm font-medium text-gray-700"
                data-cy="notification-settings-label">
                Notification Settings
              </span>
              {confOrderEmailManagementEnabled && (
                <Controller
                  name="shouldReceiveConferenceOrderNotifications"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      data-cy="shouldReceiveConferenceOrderNotifications"
                      id="shouldReceiveConferenceOrderNotifications"
                      htmlName="shouldReceiveConferenceOrderNotifications"
                      label="Should Receive Conference Order Notifications"
                      onClick={(evt) => field.onChange(evt.target.checked)}
                      value={field.value}
                    />
                  )}
                />
              )}
              {v2ConferenceEmailsEnabled && (
                <Controller
                  name="shouldReceiveAllOtherNotifications"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      data-cy="should-receive-all-other-notifications"
                      id="shouldReceiveAllOtherNotifications"
                      htmlName="shouldReceiveAllOtherNotifications"
                      label="Should Receive All Other Notifications"
                      onClick={(evt) => field.onChange(evt.target.checked)}
                      value={field.value}
                    />
                  )}
                />
              )}
            </div>
          )}
        </div>
        <div className="sm:flex sm:justify-end sm:col-span-6">
          <div className="block pt-2 space-y-2 sm:pt-0 sm:space-y-0 sm:justify-end sm:space-x-4 sm:flex">
            <Button
              onClick={() => navigate(-1)}
              className="w-full sm:w-24"
              buttonColor="gray"
              buttonStyle="secondary">
              Back
            </Button>
            <ButtonWithLoading
              disabled={syncUserMutation.isLoading}
              loading={syncUserMutation.isLoading}
              className="w-full sm:w-24"
              buttonColor="orange"
              onClick={handleSync}>
              Sync
            </ButtonWithLoading>
            <ButtonWithLoading
              disabled={updateUserProfileMutation.isLoading}
              loading={updateUserProfileMutation.isLoading}
              className="w-full sm:w-24"
              buttonColor="blue"
              type="submit">
              Save
            </ButtonWithLoading>
          </div>
        </div>
      </form>
    </div>
  );
}

export default UserProfileForm;
